import { NavLink, useLocation } from 'react-router-dom'
import LogoBrSE from '@/components/LogoBrSE'
import UserMenu from '@/components/UserMenu'
import { PlusIcon, XMarkIcon, BookIcon, FolderIcon, SettingsIcon } from '@/components/Icons'
import {
  PROJECT_MANAGEMENT_ENABLED,
  UPCOMING_MEETINGS_ENABLED,
} from '@/constants/feature-flags'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useBacklogConnectNudge } from '@/hooks/useBacklogConnectNudge'
import { useStore } from '@/store/useStore'

interface AppSidebarProps {
  onNavigate?: () => void
  onClose?: () => void
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 3 3 10.4V21h6v-6h6v6h6V10.4L12 3Z" />
    </svg>
  )
}

function HistoryIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 12a8.5 8.5 0 1 0 2.4-5.9L3 7.5" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path strokeLinecap="round" d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  )
}

function navLinkClass(isActive: boolean): string {
  return [
    'flex items-center gap-2.5 rounded-md px-4 py-2.5 text-sm transition-colors',
    isActive
      ? 'bg-blue-50 font-semibold text-[var(--accent)] dark:bg-[var(--accent-light)]'
      : 'font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]',
  ].join(' ')
}

export default function AppSidebar({ onNavigate, onClose }: AppSidebarProps) {
  const needsBacklogConnection = useBacklogConnectNudge()
  const location = useLocation()
  const { t } = useUiStrings()
  const openCreateMeetingModal = useStore((s) => s.openCreateMeetingModal)
  const isMeetingDetailPath = /^\/meetings\/[^/]+$/.test(location.pathname)

  const handleStartMeeting = () => {
    openCreateMeetingModal()
    onNavigate?.()
  }

  return (
    <div className="flex h-full flex-col px-5 py-6 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <LogoBrSE className="h-9 w-auto sm:h-10" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] md:hidden"
            aria-label={t.closeSidebar}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleStartMeeting}
        className="btn-primary mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold"
      >
        <PlusIcon className="h-4 w-4" />
        {t.startMeeting}
      </button>

      <nav className="mt-6 space-y-1.5">
        <NavLink
          to="/home"
          onClick={onNavigate}
          className={({ isActive }) => navLinkClass(isActive)}
        >
          <HomeIcon className="h-4 w-4" />
          <span>{t.home}</span>
        </NavLink>
        <NavLink
          to="/history"
          onClick={onNavigate}
          className={({ isActive }) => navLinkClass(isActive || isMeetingDetailPath)}
        >
          <HistoryIcon className="h-4 w-4" />
          <span>{t.history}</span>
        </NavLink>
        {/* Behind the same flag as the page itself: without it /upcoming just
            redirects home, and a menu item that bounces reads as a broken link. */}
        {UPCOMING_MEETINGS_ENABLED && (
          <NavLink
            to="/upcoming"
            onClick={onNavigate}
            className={({ isActive }) => navLinkClass(isActive)}
          >
            <CalendarIcon className="h-4 w-4" />
            <span>{t.upcoming}</span>
          </NavLink>
        )}
        {PROJECT_MANAGEMENT_ENABLED && (
          <NavLink
            to="/projects"
            onClick={onNavigate}
            className={({ isActive }) => navLinkClass(isActive)}
          >
            <FolderIcon className="h-4 w-4" />
            <span>{t.projectManagement}</span>
          </NavLink>
        )}
        <NavLink
          to="/glossary"
          onClick={onNavigate}
          className={({ isActive }) => navLinkClass(isActive)}
        >
          <BookIcon className="h-4 w-4" />
          <span>{t.glossary}</span>
        </NavLink>
        {/* The Backlog key lives here, and the project picker in the
            create-meeting form links straight to it. */}
        <NavLink
          to="/settings"
          onClick={onNavigate}
          className={({ isActive }) => navLinkClass(isActive)}
          // The dot says this in colour and says nothing at all to a screen
          // reader, so the link carries the state in its own name. Named here
          // rather than in a hidden span beside the label: accessible names
          // concatenate adjacent text with no separator, which comes out as
          // "SettingsBacklog account not connected". The visible label stays a
          // prefix, so voice control still matches on "Settings" (WCAG 2.5.3).
          aria-label={
            needsBacklogConnection ? `${t.settings} — ${t.backlogNudgeSidebar}` : undefined
          }
        >
          <SettingsIcon className="h-4 w-4" />
          <span>{t.settings}</span>
          {/* Shown only while this account still needs a Backlog connection.
              A connected account has nothing left for the dot to say. */}
          {needsBacklogConnection && (
            <span
              aria-hidden="true"
              className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[var(--accent)]"
              data-testid="sidebar-backlog-dot"
            />
          )}
        </NavLink>
      </nav>

      <div className="mt-auto border-t border-[var(--border-color)] pt-6">
        <UserMenu onNavigate={onNavigate} />
      </div>
    </div>
  )
}
